import { lazy } from 'react';
//import { Navigate } from 'react-router-dom';
import Loadable from 'layouts/loader/Loadable';
import LoginPage from 'views/auth/login/ViewLogin';
import { useAppSelector } from 'store/Store';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ViewClientOrders from 'views/orders/ViewClientOrders';
import TestTableView from 'views/TestTableView';
import GuestLayout from 'layouts/theme/GuestLayout';

// a function to retry loading a chunk to avoid chunk load error for out of date code
const lazyRetry = (componentImport, name) => {
  return new Promise((resolve, reject) => {
    const key = `retry-${name}-refreshed`;

    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(window.sessionStorage.getItem(key) || 'false');
    // try to import the component
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem(key, 'false'); // success so reset the refresh
        resolve(component);
      })
      .catch((error) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          window.sessionStorage.setItem(key, 'true'); // we are now going to refresh
          return window.location.reload(); // refresh the page
        }
        reject(error); // // Default error behaviour as already tried refresh
        return null;
      });
  });
};

const lazyWithRetry = (componentImport, name) => {
  return lazy(() => lazyRetry(componentImport, name));
};

/****Layouts*****/

const FullLayout = Loadable(lazyWithRetry(() => import('layouts/FullLayout'), 'FullLayout'));
/***** Pages ****/

const ViewSearch = Loadable(lazyWithRetry(() => import('views/catalog/ViewSearch'), 'ViewSearch'));

const ViewProduct = Loadable(
  lazyWithRetry(() => import('views/product/details/ViewProductDetails'), 'ViewProductDetails'),
);

const ViewGenCartOrder = Loadable(
  lazyWithRetry(() => import('views/cart/ViewGenCartOrder'), 'ViewGenCartOrder'),
);

const ProductGridPick = Loadable(
  lazyWithRetry(() => import('views/product/picker/grid/ViewPicker'), 'ViewPicker'),
);

const ViewAddClient = Loadable(
  lazyWithRetry(() => import('views/client/ViewAddClient'), 'ViewAddClient'),
);

const ViewClientDetails = Loadable(
  lazyWithRetry(() => import('views/client/ViewClientDetails'), 'ViewClientDetails'),
);

const ViewOrder = Loadable(lazyWithRetry(() => import('views/orders/ViewOrder'), 'ViewOrder'));

const ViewGuestOrder = Loadable(
  lazyWithRetry(() => import('views/orders/ViewGuestOrder'), 'ViewGuestOrder'),
);

const ViewTablePicker = Loadable(
  lazyWithRetry(() => import('views/product/picker/table/ViewTablePicker'), 'ViewTablePicker'),
);

const ViewMantCart = Loadable(
  lazyWithRetry(() => import('views/cart/ViewMantCart'), 'ViewMantCart'),
);

const ViewToast = Loadable(lazyWithRetry(() => import('views/ViewToast'), 'ViewToast'));

/*****Routes******/

const AuthRoute = ({ children }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  return isAuthenticated ? (
    { ...children }
  ) : (
    <>
      <Navigate to="/login" />
    </>
  );
};

AuthRoute.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

const ThemeRoutes = [
  {
    path: '/',
    element: (
      <AuthRoute>
        <FullLayout />
      </AuthRoute>
    ),
    children: [
      { path: '/toast', name: 'Cart', element: <ViewToast /> },
      {
        path: '/product',
        children: [
          { path: 'search', name: 'Home', element: <ViewSearch /> },
          { path: ':idProduct', name: 'Product', element: <ViewProduct /> },
        ],
      },
      {
        path: '/picker',
        children: [
          { path: '', name: 'Picker', element: <ProductGridPick /> },
          { path: ':idArticle', name: 'Picker', element: <ProductGridPick /> },
        ],
      },
      {
        path: '/tablepicker',
        element: <ViewTablePicker />,
      },
      { path: '/', name: 'Manteniment cistelles', element: <ViewMantCart /> },
      { path: '/cart', name: 'Cart', element: <ViewGenCartOrder /> },
      { path: '/clients/new', name: 'Alta client', element: <ViewAddClient /> },
      { path: '/clients/view', name: 'Detall client', element: <ViewClientDetails /> },
      { path: '/clients/orders', name: 'Ordres client', element: <ViewClientOrders /> },

      { path: '/order/:npe', name: 'Detall Ordre', element: <ViewOrder /> },
      { path: '/test/table', name: 'Test table', element: <TestTableView /> },

      //{ path: '/sample', name: 'Sample', exact: true, element: <Sample /> }
    ],
  },
  { path: '/login', element: <LoginPage /> },
  {
    path: '/guest/order',
    name: 'Guest Order',
    element: (
      <GuestLayout>
        <ViewGuestOrder />
      </GuestLayout>
    ),
  },
];

export default ThemeRoutes;
